import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  TextField,
  Typography,
  Grid,
  FormControlLabel,
  Switch,
  Tab,
  Tabs,
  Paper,
  Alert,
  Divider,
  Chip,
} from '@mui/material';
import { Save, ArrowBack, Preview } from '@mui/icons-material';
import { useQuery, useMutation } from '@apollo/client';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import {
  GET_DISEASES,
  GET_DISEASE,
  UPDATE_DISEASE,
  CREATE_DISEASE,
  SEARCH_TREATMENTS,
} from '../graphql/operations';
import { Disease, DiseaseInput, UpdateDiseaseInput } from '../types';
import ImageManager from './ImageManager';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`disease-tabpanel-${index}`}
      aria-labelledby={`disease-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const DiseaseEdit: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const isNew = !id;

  const [tabValue, setTabValue] = useState(0);
  const [formData, setFormData] = useState<DiseaseInput>({
    name: '',
    icd10_code: '',
    short_description: '',
    content: '',
    is_active: true,
  });
  const [markdownPreview, setMarkdownPreview] = useState(false);

  const { data, loading, error } = useQuery(GET_DISEASE, {
    variables: { id },
    skip: isNew,
    onCompleted: (data) => {
      if (data.getDisease) {
        const disease = data.getDisease;
        setFormData({
          name: disease.name || '',
          icd10_code: disease.icd10_code || '',
          short_description: disease.short_description || '',
          content: disease.content || '',
          is_active: disease.is_active ?? true,
        });
      }
    },
  });

  const [createDisease, { loading: creating }] = useMutation(CREATE_DISEASE, {
    update: (cache, { data }) => {
      if (data?.createDisease) {
        const existingDiseasesQuery = cache.readQuery({ query: GET_DISEASES });

        if (existingDiseasesQuery && (existingDiseasesQuery as any).listDiseases) {
          cache.writeQuery({
            query: GET_DISEASES,
            data: {
              listDiseases: {
                ...(existingDiseasesQuery as any).listDiseases,
                items: [data.createDisease, ...(existingDiseasesQuery as any).listDiseases.items],
              },
            },
          });
        }
      }
    },
    onCompleted: (data) => {
      navigate(`/diseases/${data.createDisease.id}`);
    },
    onError: (error) => {
      console.error('Error creating disease:', error);
    },
  });

  const [updateDisease, { loading: updating }] = useMutation(UPDATE_DISEASE, {
    update: (cache, { data }, { variables }) => {
      const existingDiseasesQuery = cache.readQuery({ query: GET_DISEASES });

      if (existingDiseasesQuery && variables && (existingDiseasesQuery as any).listDiseases) {
        const existingDiseases = (existingDiseasesQuery as any).listDiseases.items;
        const newDiseases = existingDiseases.map((disease: any) => {
          if (disease.id === variables.id) {
            return { ...disease, ...variables.input };
          }
          return disease;
        });

        cache.writeQuery({
          query: GET_DISEASES,
          data: {
            listDiseases: {
              ...(existingDiseasesQuery as any).listDiseases,
              items: newDiseases,
            },
          },
        });
      }
    },
    // onCompleted: () => {
    //   navigate(-1);
    // },
    onError: (error) => {
      console.error('Error updating disease:', error);
    },
  });

  const handleInputChange = (field: keyof DiseaseInput) => (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const value = event.target.type === 'checkbox'
      ? (event.target as HTMLInputElement).checked
      : event.target.value;

    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    try {
      if (isNew) {
        await createDisease({ variables: { input: formData } });
      } else {
        const updateInput: UpdateDiseaseInput = { ...formData };
        await updateDisease({ variables: { id, input: updateInput } });
      }
    } catch (error) {
      console.error('Error saving disease:', error);
    }
  };

  const handleBackClick = () => {
    navigate(-1);
  };

  const disease = data?.getDisease;

  if (!isNew && loading) return <Typography>Loading disease...</Typography>;
  if (!isNew && error) return <Alert severity="error">Error loading disease: {error.message}</Alert>;

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box display="flex" alignItems="center" gap={2}>
          <Button
            startIcon={<ArrowBack />}
            onClick={handleBackClick}
            variant="outlined"
          >
            Back to Diseases
          </Button>
          <Typography variant="h4">
            {isNew ? 'Create New Disease' : `Edit: ${disease?.name}`}
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<Save />}
          onClick={handleSave}
          disabled={creating || updating || !formData.name.trim()}
        >
          {creating || updating ? 'Saving...' : 'Save'}
        </Button>
      </Box>

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={tabValue}
          onChange={(_, newValue) => setTabValue(newValue)}
          aria-label="disease edit tabs"
        >
          <Tab label="Basic Info" />
          <Tab label="Content" />
          {!isNew && <Tab label="Images" />}
          {!isNew && <Tab label="Treatments" />}
        </Tabs>
      </Paper>

      {/* Tab Panels */}
      <TabPanel value={tabValue} index={0}>
        <Card>
          <CardContent>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Disease Name"
                  value={formData.name}
                  onChange={handleInputChange('name')}
                  required
                  helperText="Enter the full name of the disease"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="ICD-10 Code"
                  value={formData.icd10_code}
                  onChange={handleInputChange('icd10_code')}
                  helperText="Optional ICD-10 diagnostic code"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Short Description"
                  value={formData.short_description}
                  onChange={handleInputChange('short_description')}
                  multiline
                  rows={3}
                  helperText="Brief summary for search results and cards"
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.is_active}
                      onChange={(e) => setFormData(prev => ({ ...prev, is_active: e.target.checked }))}
                    />
                  }
                  label="Active"
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        <Card>
          <CardContent>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6">Content (Markdown)</Typography>
              <Button
                startIcon={<Preview />}
                onClick={() => setMarkdownPreview(!markdownPreview)}
                variant="outlined"
                size="small"
              >
                {markdownPreview ? 'Edit' : 'Preview'}
              </Button>
            </Box>
            
            {markdownPreview ? (
              <Paper variant="outlined" sx={{ p: 2, minHeight: 400, maxHeight: 600, overflow: 'auto' }}>
                <ReactMarkdown>{formData.content || 'No content yet...'}</ReactMarkdown>
              </Paper>
            ) : (
              <TextField
                fullWidth
                multiline
                rows={20}
                value={formData.content}
                onChange={handleInputChange('content')}
                placeholder="Write detailed content about the disease in Markdown format...

Example:
# Disease Overview
This is a common skin condition...

## Symptoms
- Symptom 1
- Symptom 2

## Causes
The main causes include...

## Treatment Options
Treatment typically involves..."
                variant="outlined"
              />
            )}
          </CardContent>
        </Card>
      </TabPanel>

      {!isNew && (
        <TabPanel value={tabValue} index={2}>
          <ImageManager diseaseId={id!} />
        </TabPanel>
      )}

      {!isNew && (
        <TabPanel value={tabValue} index={3}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Linked Treatments ({disease?.treatments?.length || 0})
              </Typography>
              
              {disease?.treatments && disease.treatments.length > 0 ? (
                <Grid container spacing={2}>
                  {disease.treatments.map((treatment: any) => (
                    <Grid item xs={12} md={6} key={treatment.id}>
                      <Card variant="outlined" sx={{ height: '100%' }}>
                        <CardContent sx={{ p: 2 }}>
                          <Box display="flex" justifyContent="space-between" alignItems="start" mb={1}>
                            <Typography variant="h6" component="h3" sx={{ fontWeight: 600 }}>
                              {treatment.name}
                            </Typography>
                            <Box display="flex" gap={1}>
                              <Chip
                                label={treatment.treatment_type || 'General'}
                                size="small"
                                color="primary"
                                variant="outlined"
                              />
                              <Chip
                                label={treatment.is_active ? 'Active' : 'Inactive'}
                                size="small"
                                color={treatment.is_active ? 'success' : 'default'}
                                variant={treatment.is_active ? 'filled' : 'outlined'}
                              />
                            </Box>
                          </Box>
                          
                          {treatment.description && (
                            <Typography 
                              variant="body2" 
                              color="textSecondary" 
                              sx={{ mb: 2, lineHeight: 1.4 }}
                            >
                              {treatment.description.length > 150 
                                ? `${treatment.description.substring(0, 150)}...`
                                : treatment.description
                              }
                            </Typography>
                          )}
                          
                          {(treatment.regular_price || treatment.sale_price) && (
                            <Box mb={2}>
                              <Typography variant="subtitle2" color="textSecondary" sx={{ mb: 0.5 }}>
                                Pricing:
                              </Typography>
                              <Box display="flex" gap={2} alignItems="center">
                                {treatment.sale_price && (
                                  <Typography variant="h6" color="primary" sx={{ fontWeight: 600 }}>
                                    ${treatment.sale_price}
                                  </Typography>
                                )}
                                {treatment.regular_price && (
                                  <Typography 
                                    variant="body2" 
                                    color="textSecondary"
                                    sx={{ 
                                      textDecoration: treatment.sale_price ? 'line-through' : 'none'
                                    }}
                                  >
                                    ${treatment.regular_price}
                                  </Typography>
                                )}
                              </Box>
                            </Box>
                          )}
                          
                          {treatment.instructions && (
                            <Box mb={2}>
                              <Typography variant="subtitle2" color="textSecondary" sx={{ mb: 0.5 }}>
                                Instructions:
                              </Typography>
                              <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                                {treatment.instructions.length > 100
                                  ? `${treatment.instructions.substring(0, 100)}...`
                                  : treatment.instructions
                                }
                              </Typography>
                            </Box>
                          )}
                          
                          <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Typography variant="caption" color="textSecondary">
                              Added: {new Date(treatment.created_at).toLocaleDateString()}
                            </Typography>
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => navigate(`/treatments/${treatment.id}/edit`)}
                            >
                              Edit Treatment
                            </Button>
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              ) : (
                <Box textAlign="center" py={4}>
                  <Typography color="textSecondary" variant="body1" sx={{ mb: 2 }}>
                    No treatments are currently linked to this disease.
                  </Typography>
                  <Typography color="textSecondary" variant="body2">
                    Treatment linking functionality can be added to connect relevant treatments to this disease.
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </TabPanel>
      )}
    </Box>
  );
};

export default DiseaseEdit;
